import { ChangeDetectionStrategy, Component, computed, viewChild } from "@angular/core";
import { ModalComponent } from "../modal.component";
import { S25QrCodeComponent } from "../../../standalone/s25.qr.code.component";
import {
    buildFormPublicStartUrl,
    buildFormQrDownloadFileName,
} from "../../data-ingestion/admin/ingestion.form.public.url.util";
import { downloadQrPngFromCanvas } from "../../data-ingestion/admin/ingestion.form.qr.download.util";
import { S25Util } from "../../../util/s25-util";

export type IngestionFormQrModalData = {
    ingestionPointId: number;
    formName?: string;
};

@Component({
    template: `
        <div class="s25-ng">
            <s25-modal-header [data]="params()"></s25-modal-header>
            <div class="modal-body">
                <p class="form-help-text">
                    Scanning opens the public form link. Authless forms ask for name and email before filling; other
                    forms show as unavailable when the scanner is not signed in.
                </p>
                <div class="qr-wrap">
                    <s25-ng-qr-code #qrCode [text]="publicUrl()" [scale]="2" />
                </div>
                <div class="url-section">
                    <label class="url-label" for="ingestion-form-qr-public-link">Public link</label>
                    <div class="url-row">
                        <input
                            id="ingestion-form-qr-public-link"
                            class="c-input url-input"
                            type="text"
                            readonly
                            [value]="publicUrl()"
                        />
                        <button
                            type="button"
                            class="url-copy-btn"
                            title="Copy link"
                            aria-label="Copy public link to clipboard"
                            (click)="copyPublicLink()"
                        >
                            <s25-ng-icon [type]="'copy2'" [ariaLabel]="false" />
                        </button>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <s25-ng-button [type]="'outline'" (clicked)="downloadPng()">Download PNG</s25-ng-button>
            </div>
        </div>
    `,
    styles: `
        .qr-wrap {
            display: flex;
            justify-content: center;
            margin: 1rem 0 1.5rem;
        }

        .url-section {
            margin-top: 0.5rem;
            padding: 0.75rem 0 0.25rem;
        }

        .url-label {
            display: block;
            font-weight: 600;
            margin-bottom: 0.5rem;
        }

        .url-row {
            display: flex;
            align-items: stretch;
            gap: 0.5rem;
        }

        .url-input {
            flex: 1;
            min-width: 0;
            padding: 0.5rem 0.65rem;
        }

        .url-copy-btn {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 2.5rem;
            padding: 0 0.65rem;
            border: 1px solid var(--color--border);
            border-radius: var(--border-radius);
            background: var(--color--surface-2-bg);
            color: var(--color--link-text);
            cursor: pointer;
            --icon-size: 1.25rem;
        }

        .url-copy-btn:hover,
        .url-copy-btn:focus {
            background: var(--color--primary-flat-bg);
            border-color: var(--color--link-text);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ModalIngestionFormQrComponent extends ModalComponent<IngestionFormQrModalData> {
    readonly qrCode = viewChild<S25QrCodeComponent>("qrCode");

    readonly publicUrl = computed(() => {
        const pointId = this.params()?.ingestionPointId;
        return pointId ? buildFormPublicStartUrl(pointId) : "";
    });

    downloadPng = () => {
        const canvas = this.qrCode()?.canvas()?.nativeElement;
        const modalParams = this.params();
        if (!canvas || !modalParams?.ingestionPointId) return;
        downloadQrPngFromCanvas(
            canvas,
            buildFormQrDownloadFileName(modalParams.formName, modalParams.ingestionPointId),
        );
    };

    copyPublicLink = () => {
        const url = this.publicUrl();
        if (url) {
            S25Util.copyToClipboard(url);
        }
    };
}
